import { Head, useForm, router } from '@inertiajs/react'
import { useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import { DashboardPageProps, PhysicalMailFolder, BusinessRef } from '@/types'
import { Plus, Pencil, Trash2 } from 'lucide-react'

type Props = {
  business: BusinessRef
  folders: PhysicalMailFolder[]
}

export default function BusinessPhysicalMailFoldersIndex({ business, folders }: DashboardPageProps<Props>) {
  const basePath = useBasePath()
  const confirm = useConfirm()
  const form = useForm({ name: '' })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    form.post(`${basePath}/businesses/${business.id}/physical_mail_folders`, {
      onSuccess: () => form.reset(),
    })
  }

  function startEdit(folder: PhysicalMailFolder) {
    setEditingId(folder.id)
    setEditingName(folder.name)
  }

  function handleRename(folder: PhysicalMailFolder) {
    router.patch(
      `${basePath}/businesses/${business.id}/physical_mail_folders/${folder.id}`,
      { physical_mail_folder: { name: editingName } },
      { onSuccess: () => setEditingId(null) }
    )
  }

  async function handleDelete(folder: PhysicalMailFolder) {
    const ok = await confirm({
      title: 'Delete folder',
      description: `Delete folder "${folder.name}"? Mails in this folder won't be deleted.`,
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (ok) {
      router.delete(`${basePath}/businesses/${business.id}/physical_mail_folders/${folder.id}`)
    }
  }

  return (
    <DashboardLayout
      title="Mail Folders"
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: business.name, href: `businesses/${business.id}` },
        { label: 'Physical Mail', href: `businesses/${business.id}/physical_mails` },
        { label: 'Folders' },
      ]}
    >
      <Head title="Mail Folders" />

      <div className="space-y-6 max-w-md">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Mail Folders</h1>
          <p className="text-sm text-muted-foreground mt-1">Organise your physical mail into folders.</p>
        </div>

        {/* Create folder */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">New folder</h2>
          </div>
          <form onSubmit={handleCreate} className="px-4 py-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="folder-name">Folder name</Label>
              <Input
                id="folder-name"
                value={form.data.name}
                onChange={(e) => form.setData('name', e.target.value)}
                placeholder="e.g. HMRC, Companies House…"
              />
              {form.errors.name && (
                <p className="text-sm text-destructive">{form.errors.name}</p>
              )}
            </div>
            <Button type="submit" size="sm" disabled={form.processing}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              {form.processing ? 'Creating…' : 'Create folder'}
            </Button>
          </form>
        </section>

        {/* Folder list */}
        {folders.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Your folders</h2>
            </div>
            <div className="divide-y">
              {folders.map((folder) => (
                <div key={folder.id} className="flex items-center justify-between px-4 py-3">
                  {editingId === folder.id ? (
                    <div className="flex flex-1 items-center gap-2">
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="h-8"
                        autoFocus
                      />
                      <Button size="sm" onClick={() => handleRename(folder)}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-foreground">{folder.name}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => startEdit(folder)}
                        >
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleDelete(folder)}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  )
}
